import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Workflow,
  Lightbulb,
  AlertTriangle,
  Clock,
  MapIcon,
  X,
  Wrench,
  Target,
  CheckCircle2,
  HelpCircle,
  GitCompare,
  Network,
  Sparkles,
  Loader,
  DollarSign,
  TrendingUp,
  Database
} from 'lucide-react';
import { OptimizationOpportunity } from '../../services/processOptimizationAPI';
import GroundedStatusBadge from './GroundedStatusBadge';
import WorkflowViewer from './WorkflowViewer';
import AIAssistant from './AIAssistant';
import ImplementationFactorsAnalysis from './ImplementationFactorsAnalysis';
import PhasedRoadmap from './PhasedRoadmap';
import IntegrationAnalysis from './IntegrationAnalysis';
import ApproachBadge from './ApproachBadge';
import ComplianceRequirements from './ComplianceRequirements';
import EnhancedComplianceRequirements from './EnhancedComplianceRequirements';
import ProcessFlowComparison from './ProcessFlowComparison';
import MRMClassification from './MRMClassification';
import SODAnalysis from './SODAnalysis';
import ArchitectureDiagram from './ArchitectureDiagram';
import AIWorkflowModal from './AIWorkflowModal';
import StakeholderRACIMatrix from './StakeholderRACIMatrix';
import ChangeImpactAssessment from './ChangeImpactAssessment';
import ResistanceRiskFlags from './ResistanceRiskFlags';

type ViewMode = 'all' | 'business' | 'compliance';

interface OpportunityCardProps {
  opportunity: OptimizationOpportunity;
  projectId: number;
  index?: number;
  viewMode?: ViewMode;
  onOpenImportModal?: () => void;
  isReviewed?: boolean;
  onMarkReviewed?: (id: number) => void;
  hideGroundedFinancials?: boolean;
}

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity, projectId, index = 0, viewMode = 'all', onOpenImportModal, isReviewed = false, onMarkReviewed, hideGroundedFinancials = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'workflow' | 'compliance' | 'implementation'>('overview');
  const [showWorkflows, setShowWorkflows] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [showProcessFlow, setShowProcessFlow] = useState(false);
  const [showArchitecture, setShowArchitecture] = useState(false);
  const [showAIWorkflow, setShowAIWorkflow] = useState(false);
  const [showPrerequisites, setShowPrerequisites] = useState(false);
  const [showUnlocks, setShowUnlocks] = useState(false);
  const [showHistogram, setShowHistogram] = useState(true);
  const [mrmApprovalStatus, setMrmApprovalStatus] = useState(opportunity.mrm_approval_status || 'pending');
  const [sodApprovalStatus, setSodApprovalStatus] = useState(opportunity.sod_approval_status || 'pending');

  const handleMrmApprovalChange = (status: 'approved' | 'rejected') => {
    setMrmApprovalStatus(status);
  };

  const handleSodApprovalChange = (status: 'approved' | 'rejected') => {
    setSodApprovalStatus(status);
  };

  const getComplexityColor = (complexity: string) => {
    const colors: any = {
      'low': 'bg-green-100 text-green-800 border-green-300',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'high': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[complexity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getInefficiencyIcon = (type: string) => {
    const icons: any = {
      'redundancy': '🔄',
      'sequential_bottleneck': '⚡',
      'manual_pattern': '✋',
      'loop_detection': '🔁',
      'handoff_delay': '🤝',
      'exception_handling': '⚠️'
    };
    return icons[type] || '📋';
  };

  const getInefficiencyLabel = (type: string) => {
    const labels: any = {
      'redundancy': 'Redundancy',
      'sequential_bottleneck': 'Sequential Bottleneck',
      'manual_pattern': 'Manual Process',
      'loop_detection': 'Rework Loop',
      'handoff_delay': 'Handoff Delay',
      'exception_handling': 'Exception Handling'
    };
    return labels[type] || type;
  };

  const cardBgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
  const hasGovernanceScores = opportunity.governance_complexity_score !== undefined;
  const hasPrerequisites = opportunity.dependencies?.prerequisites && opportunity.dependencies.prerequisites.length > 0;
  const hasUnlocks = opportunity.dependencies?.blocks && opportunity.dependencies.blocks.length > 0;

  return (
    <div className={`${cardBgColor} border rounded-xl shadow-sm hover:shadow-md transition-all duration-300 ${isReviewed ? 'border-green-300' : 'border-gray-200'}`}>
      <div
        className="p-5 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{getInefficiencyIcon(opportunity.inefficiency_type)}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-gray-900">{opportunity.title}</h3>
                  {isReviewed && (
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Reviewed
                    </span>
                  )}
                  <GroundedStatusBadge
                    isGrounded={opportunity.data_source === 'grounded'}
                    groundedDate={opportunity.grounded_date}
                    size="sm"
                  />
                </div>
                <span className="text-sm text-gray-600 font-medium">
                  {getInefficiencyLabel(opportunity.inefficiency_type)}
                </span>
              </div>
            </div>

            {/* Collapsed badge row — essentials only */}
            <div className="flex flex-wrap gap-2 mt-1">
              <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${getComplexityColor(opportunity.implementation_complexity)}`}>
                {opportunity.implementation_complexity.toUpperCase()}
              </span>
              {opportunity.estimated_time_reduction && (
                <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {opportunity.estimated_time_reduction}
                </span>
              )}
              {hasPrerequisites && (
                <span className="px-2.5 py-1 bg-orange-50 text-orange-600 rounded-md text-xs font-medium border border-orange-200 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {opportunity.dependencies!.prerequisites.length} prerequisite{opportunity.dependencies!.prerequisites.length > 1 ? 's' : ''}
                </span>
              )}
              {/* Key financial metric — one number only (hidden in AI Estimates step) */}
              {!hideGroundedFinancials && opportunity.data_source === 'grounded' && opportunity.total_annual_benefit ? (
                <>
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-md text-xs font-semibold border border-green-200 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    ${(Number(opportunity.total_annual_benefit) / 1000).toFixed(0)}K/yr
                  </span>
                  {opportunity.roi_3year_pct != null && (
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200">
                      {Number(opportunity.roi_3year_pct).toFixed(0)}% ROI
                    </span>
                  )}
                </>
              ) : null}
              {opportunity.recommended_approach && (
                <ApproachBadge
                  approach={opportunity.recommended_approach}
                  platform={opportunity.recommended_platform}
                  size="sm"
                />
              )}
            </div>
          </div>

          <button
            className="ml-4 p-2 hover:bg-gray-200 rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? (
              <ChevronUp className="w-6 h-6 text-gray-600" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 bg-white">
          {/* Tab Bar */}
          <div className="flex border-b border-gray-200 px-6 pt-4">
            {(['overview', 'workflow', 'compliance', 'implementation'] as const).map((tab) => (
              <button
                key={tab}
                onClick={(e) => { e.stopPropagation(); setActiveTab(tab); }}
                className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors mr-2 ${
                  activeTab === tab
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-6 space-y-6">

          {/* ── OVERVIEW TAB ── */}
          {activeTab === 'overview' && (
            <>
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <h4 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
              <Target className="w-5 h-5" />
              PROBLEM
            </h4>
            <p className="text-gray-700 text-sm leading-relaxed">{opportunity.description}</p>
          </div>

          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
            <h4 className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              SOLUTION
            </h4>
            <p className="text-gray-700 text-sm leading-relaxed">{opportunity.solution_approach}</p>
          </div>

          {opportunity.affected_steps && opportunity.affected_steps.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                AFFECTED PROCESS STEPS
              </h4>
              <div className="bg-white border-2 border-gray-300 rounded-lg p-4 shadow-sm">
                <ul className="space-y-2">
                  {opportunity.affected_steps.map((step: any, idx: number) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>
                        {step.id && <strong className="text-blue-800">{step.id} - </strong>}
                        <strong>{step.name || step.stepName}</strong>
                        {step.department && <span className="text-gray-500"> ({step.department})</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Grounded Financial Metrics (hidden in AI Estimates step) */}
          {!hideGroundedFinancials && opportunity.data_source === 'grounded' && opportunity.total_annual_benefit ? (
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border-2 border-emerald-300 rounded-lg p-4">
              <h4 className="text-sm font-bold text-emerald-800 mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                BUSINESS CASE
                <span className="ml-auto text-xs font-normal text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                  ✓ Grounded Data • v{opportunity.calculated_at ? new Date(opportunity.calculated_at).toLocaleDateString() : ''}
                </span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3 border border-emerald-200 text-center">
                  <div className="text-xs text-gray-500 mb-1">Annual Benefit</div>
                  <div className="text-lg font-bold text-emerald-600">
                    {(() => { const v = Number(opportunity.total_annual_benefit); return v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`; })()}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-200 text-center">
                  <div className="text-xs text-gray-500 mb-1">NPV (3-Year)</div>
                  <div className="text-lg font-bold text-blue-600">
                    {opportunity.npv_3year != null
                      ? (() => { const v = Number(opportunity.npv_3year); return v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`; })()
                      : '-'}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-purple-200 text-center">
                  <div className="text-xs text-gray-500 mb-1">ROI (3-Year)</div>
                  <div className="text-lg font-bold text-purple-600">
                    {opportunity.roi_3year_pct != null ? `${Number(opportunity.roi_3year_pct).toFixed(0)}%` : '-'}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                  <div className="text-xs text-gray-500 mb-1">Payback</div>
                  <div className="text-lg font-bold text-amber-600">
                    {opportunity.payback_months != null ? `${Number(opportunity.payback_months).toFixed(1)} mo` : '-'}
                  </div>
                </div>
              </div>
              {opportunity.annual_hours_saved != null && (
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-600">
                  <div className="flex justify-between bg-white rounded px-3 py-2 border border-gray-200">
                    <span>Hours Saved/Year</span>
                    <span className="font-bold text-gray-800">{Number(opportunity.annual_hours_saved).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                  </div>
                  <div className="flex justify-between bg-white rounded px-3 py-2 border border-gray-200">
                    <span>Weighted Rate</span>
                    <span className="font-bold text-gray-800">${Number(opportunity.weighted_hourly_rate).toFixed(0)}/hr</span>
                  </div>
                </div>
              )}

              {/* Monte Carlo Sensitivity Analysis */}
              {opportunity.mc_npv_p10 != null && (
                <div className="mt-4 pt-4 border-t border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Sensitivity Analysis</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {opportunity.mc_n_simulations ? `${(opportunity.mc_n_simulations / 1000).toFixed(0)}K simulations` : '10K simulations'}
                    </span>
                  </div>
                  <div className="bg-white rounded-lg border border-emerald-200 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left px-3 py-2 text-gray-600 font-semibold">Metric</th>
                          <th className="text-center px-3 py-2 text-red-600 font-semibold">P10 Pessimistic</th>
                          <th className="text-center px-3 py-2 text-blue-600 font-semibold">P50 Median</th>
                          <th className="text-center px-3 py-2 text-green-600 font-semibold">P90 Optimistic</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="px-3 py-2 text-gray-600 font-medium">NPV (3yr)</td>
                          <td className="text-center px-3 py-2 text-red-600 font-bold">
                            {(() => { const v = Number(opportunity.mc_npv_p10); return v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`; })()}
                          </td>
                          <td className="text-center px-3 py-2 text-blue-600 font-bold">
                            {(() => { const v = Number(opportunity.mc_npv_p50); return v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`; })()}
                          </td>
                          <td className="text-center px-3 py-2 text-green-600 font-bold">
                            {(() => { const v = Number(opportunity.mc_npv_p90); return v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`; })()}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="px-3 py-2 text-gray-600 font-medium">ROI (3yr)</td>
                          <td className="text-center px-3 py-2 text-red-600 font-bold">{Number(opportunity.mc_roi_p10).toFixed(0)}%</td>
                          <td className="text-center px-3 py-2 text-blue-600 font-bold">{Number(opportunity.mc_roi_p50).toFixed(0)}%</td>
                          <td className="text-center px-3 py-2 text-green-600 font-bold">{Number(opportunity.mc_roi_p90).toFixed(0)}%</td>
                        </tr>
                        {opportunity.mc_payback_p50 != null && (
                          <tr>
                            <td className="px-3 py-2 text-gray-600 font-medium">Payback</td>
                            <td className="text-center px-3 py-2 text-red-600 font-bold">{Number(opportunity.mc_payback_p90).toFixed(1)} mo</td>
                            <td className="text-center px-3 py-2 text-blue-600 font-bold">{Number(opportunity.mc_payback_p50).toFixed(1)} mo</td>
                            <td className="text-center px-3 py-2 text-green-600 font-bold">{Number(opportunity.mc_payback_p10).toFixed(1)} mo</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {opportunity.mc_prob_positive_roi != null && (
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                        opportunity.mc_prob_positive_roi >= 0.9
                          ? 'bg-green-100 text-green-700 border-green-300'
                          : opportunity.mc_prob_positive_roi >= 0.7
                          ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                          : 'bg-red-100 text-red-700 border-red-300'
                      }`}>
                        {(opportunity.mc_prob_positive_roi * 100).toFixed(0)}% probability positive ROI
                      </span>
                    )}
                    {opportunity.mc_prob_payback_12mo != null && opportunity.mc_prob_payback_12mo > 0 && (
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                        opportunity.mc_prob_payback_12mo >= 0.5
                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                          : 'bg-gray-100 text-gray-600 border-gray-300'
                      }`}>
                        {(opportunity.mc_prob_payback_12mo * 100).toFixed(0)}% payback within 12 months
                      </span>
                    )}
                  </div>

                  {/* Histogram Toggle */}
                  {opportunity.mc_histogram_data && (
                    <div className="mt-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowHistogram(!showHistogram); }}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {showHistogram ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {showHistogram ? 'Hide' : 'Show'} Distribution Charts
                      </button>

                      {showHistogram && (
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {([
                            { label: 'NPV', bins: opportunity.mc_histogram_data.npv,     color: '#3b82f6' },
                            { label: 'ROI', bins: opportunity.mc_histogram_data.roi,     color: '#8b5cf6' },
                            { label: 'Payback', bins: opportunity.mc_histogram_data.payback, color: '#f59e0b' },
                          ] as { label: string; bins: { bin_start: number; bin_end: number; count: number }[]; color: string }[])
                            .filter(({ bins }) => bins && bins.length > 0)
                            .map(({ label, bins, color }) => {
                              const maxCount = Math.max(...bins.map(b => b.count));
                              const H = 100;
                              return (
                                <div key={label} className="bg-white rounded border border-gray-200 p-2">
                                  <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
                                  <svg
                                    viewBox={`0 0 ${bins.length * 6} ${H}`}
                                    className="w-full block"
                                    preserveAspectRatio="none"
                                    style={{ height: 100 }}
                                  >
                                    {bins.map((bin, i) => (
                                      <rect
                                        key={i}
                                        x={i * 6}
                                        y={H - (bin.count / maxCount) * H}
                                        width={5.5}
                                        height={(bin.count / maxCount) * H}
                                        fill={color}
                                        opacity={0.5 + (bin.count / maxCount) * 0.5}
                                      />
                                    ))}
                                  </svg>
                                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                                    <span>P10</span>
                                    <span>P90</span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 flex items-center gap-2">
                <span>~ AI Estimate</span>
                <span>—</span>
                <span>Upload Metrics Data to get CFO-grade financial calculations</span>
              </p>
            </div>
          )}

            </>
          )}

          {/* Mark as reviewed — shown at bottom of overview */}
          {activeTab === 'overview' && !isReviewed && onMarkReviewed && (
            <div className="pt-2 border-t border-gray-100 flex justify-end">
              <button
                onClick={(e) => { e.stopPropagation(); onMarkReviewed(opportunity.id); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-green-100 hover:text-green-700 rounded-lg transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark as Reviewed
              </button>
            </div>
          )}

          {/* ── WORKFLOW TAB ── */}
          {activeTab === 'workflow' && (
            <>
              {opportunity.ai_integration_opportunities && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h4 className="text-sm font-bold text-purple-900 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      AI INTEGRATION OPPORTUNITY
                    </h4>
                    <button
                      onClick={() => setShowAIWorkflow(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white rounded-lg hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 transition-all font-bold shadow-lg hover:shadow-xl text-xs whitespace-nowrap animate-pulse"
                    >
                      <Sparkles className="w-4 h-4" />
                      View AI Workflow
                    </button>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{opportunity.ai_integration_opportunities}</p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => setShowProcessFlow(true)}
                  className="w-full flex items-center gap-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg hover:bg-green-100 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <GitCompare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-green-800 text-sm">Process Flow</div>
                    <div className="text-xs text-green-600 mt-0.5">Compare before & after process steps</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-green-500 ml-auto -rotate-90" />
                </button>

                <button
                  onClick={() => setShowArchitecture(true)}
                  className="w-full flex items-center gap-4 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Network className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-purple-800 text-sm">Architecture</div>
                    <div className="text-xs text-purple-600 mt-0.5">Technical integration & system architecture diagram</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-purple-500 ml-auto -rotate-90" />
                </button>

                <button
                  onClick={() => setShowWorkflows(true)}
                  className="w-full flex items-center gap-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Workflow className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-blue-800 text-sm">Implementation Guide</div>
                    <div className="text-xs text-blue-600 mt-0.5">Step-by-step workflow implementation guide</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-blue-500 ml-auto -rotate-90" />
                </button>

                <button
                  onClick={() => setShowAssistant(true)}
                  className="w-full flex items-center gap-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg hover:bg-amber-100 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-amber-800 text-sm">Troubleshoot with Jarvis</div>
                    <div className="text-xs text-amber-600 mt-0.5">AI-powered troubleshooting & implementation Q&A</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-amber-500 ml-auto -rotate-90" />
                </button>
              </div>
            </>
          )}

          {/* ── COMPLIANCE TAB ── (MRM + SOD always mounted when expanded, hidden until tab active) */}
          <div className={activeTab === 'compliance' ? 'space-y-6' : 'hidden'}>
            {opportunity.compliance_requirements && (
              'metadata' in opportunity.compliance_requirements ||
              'control_activities' in opportunity.compliance_requirements ||
              'validation_monitoring' in opportunity.compliance_requirements ? (
                <EnhancedComplianceRequirements
                  complianceRequirements={opportunity.compliance_requirements as any}
                  implementationComplexity={opportunity.implementation_complexity}
                />
              ) : (
                <ComplianceRequirements
                  complianceRequirements={opportunity.compliance_requirements as any}
                  implementationComplexity={opportunity.implementation_complexity}
                />
              )
            )}

            <MRMClassification
              opportunityId={opportunity.id}
              projectId={projectId}
              opportunityTitle={opportunity.title}
              solutionApproach={opportunity.solution_approach}
              aiIntegrationOpportunities={opportunity.ai_integration_opportunities}
              viewMode={viewMode}
              approvalStatus={mrmApprovalStatus}
              onApprovalChange={handleMrmApprovalChange}
            />

            <SODAnalysis
              opportunityId={opportunity.id}
              projectId={projectId}
              affectedSteps={opportunity.affected_steps || []}
              solutionApproach={opportunity.solution_approach}
              viewMode={viewMode}
              approvalStatus={sodApprovalStatus}
              onApprovalChange={handleSodApprovalChange}
            />

            {opportunity.ai_regulatory_notes && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <h4 className="text-sm font-bold text-yellow-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  REGULATORY CONSIDERATIONS
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">{opportunity.ai_regulatory_notes}</p>
              </div>
            )}
          </div>

          {/* ── IMPLEMENTATION TAB ── */}
          {activeTab === 'implementation' && (
            <>
              {(opportunity.primary_tools && opportunity.primary_tools.length > 0) || (opportunity.integration_requirements && opportunity.integration_requirements.length > 0) ? (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    CONNECTORS & TOOLS
                  </h4>
                  {opportunity.primary_tools && opportunity.primary_tools.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-blue-700 mb-2">Recommended Tools:</div>
                      <div className="flex flex-wrap gap-2">
                        {opportunity.primary_tools.map((tool, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold rounded-full shadow-md">{tool}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {opportunity.integration_requirements && opportunity.integration_requirements.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-blue-700 mb-2">Integration Requirements:</div>
                      <div className="flex flex-wrap gap-2">
                        {opportunity.integration_requirements.map((req, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-white text-blue-700 text-xs font-bold rounded-lg border-2 border-blue-300">
                            {typeof req === 'string' ? req : req.system || JSON.stringify(req)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {hasPrerequisites && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowPrerequisites(!showPrerequisites)}
                    className="w-full p-4 flex items-center justify-between hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <h4 className="text-sm font-bold text-orange-800">⚠️ RECOMMENDED PREREQUISITES ({opportunity.dependencies!.prerequisites.length})</h4>
                    </div>
                    {showPrerequisites ? <ChevronUp className="w-5 h-5 text-orange-600" /> : <ChevronDown className="w-5 h-5 text-orange-600" />}
                  </button>
                  {showPrerequisites && (
                    <div className="p-4 pt-0 space-y-3">
                      <p className="text-xs text-orange-700 mb-3">Consider completing these automations first for easier implementation:</p>
                      {[...opportunity.dependencies!.prerequisites]
                        .sort((a: any, b: any) => {
                          const order: { [key: string]: number } = { required: 0, recommended: 1 };
                          return (order[a.dependency_strength] ?? 2) - (order[b.dependency_strength] ?? 2);
                        })
                        .map((dep: any, idx: number) => (
                          <div key={idx} className="bg-white rounded-lg p-3 border border-orange-200">
                            <div className="flex items-start gap-2">
                              <div className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-sm text-gray-900">{dep.prerequisite_opportunity?.title || `Opportunity #${dep.depends_on_opportunity_id}`}</span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${dep.dependency_strength === 'required' ? 'bg-red-100 text-red-700 border border-red-300' : dep.dependency_strength === 'recommended' ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' : 'bg-gray-100 text-gray-700 border border-gray-300'}`}>{dep.dependency_strength}</span>
                                </div>
                                <p className="text-xs text-gray-600">{dep.reason}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {hasUnlocks && (
                <div className="bg-green-50 border-2 border-green-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowUnlocks(!showUnlocks)}
                    className="w-full p-4 flex items-center justify-between hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <h4 className="text-sm font-bold text-green-800">✅ UNLOCKS ({opportunity.dependencies!.blocks.length})</h4>
                    </div>
                    {showUnlocks ? <ChevronUp className="w-5 h-5 text-green-600" /> : <ChevronDown className="w-5 h-5 text-green-600" />}
                  </button>
                  {showUnlocks && (
                    <div className="p-4 pt-0 space-y-3">
                      <p className="text-xs text-green-700 mb-3">Completing this automation will enable:</p>
                      {opportunity.dependencies!.blocks.map((dep: any, idx: number) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-green-200">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <span className="font-semibold text-sm text-gray-900">{dep.blocked_opportunity?.title || `Opportunity #${dep.source_opportunity_id}`}</span>
                              <p className="text-xs text-gray-600 mt-1">{dep.reason}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <StakeholderRACIMatrix
                opportunity={{
                  id: opportunity.id,
                  title: opportunity.title,
                  integration_complexity_score: opportunity.integration_complexity_score,
                  regulatory_risk_score: opportunity.regulatory_risk_score,
                  governance_complexity_score: opportunity.governance_complexity_score,
                  affected_steps: opportunity.affected_steps,
                  compliance_requirements: opportunity.compliance_requirements,
                  stakeholder_analysis: (opportunity as any).stakeholder_analysis
                }}
              />

              <ChangeImpactAssessment
                opportunity={{
                  id: opportunity.id,
                  title: opportunity.title,
                  affected_steps: opportunity.affected_steps,
                  compliance_requirements: opportunity.compliance_requirements,
                  implementation_complexity: opportunity.implementation_complexity,
                  change_impact_data: (opportunity as any).change_impact_data
                }}
              />

              <ResistanceRiskFlags
                opportunity={{
                  id: opportunity.id,
                  title: opportunity.title,
                  inefficiency_type: opportunity.inefficiency_type,
                  affected_steps: opportunity.affected_steps,
                  implementation_complexity: opportunity.implementation_complexity,
                  integration_complexity_score: opportunity.integration_complexity_score,
                  governance_complexity_score: opportunity.governance_complexity_score,
                  resistance_risk_flags: (opportunity as any).resistance_risk_flags
                }}
              />

              {hasGovernanceScores && (
                <ImplementationFactorsAnalysis
                  governanceScore={opportunity.governance_complexity_score!}
                  automationReadiness={opportunity.automation_readiness_score!}
                  integrationComplexity={opportunity.integration_complexity_score!}
                  regulatoryRisk={opportunity.regulatory_risk_score!}
                  governanceAnalysis={opportunity.governance_analysis}
                />
              )}

              {opportunity.integration_analysis && (
                <IntegrationAnalysis integrationAnalysis={opportunity.integration_analysis} />
              )}

              {opportunity.phased_rollout_plan && (
                <button
                  onClick={() => setShowRoadmap(true)}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full hover:from-indigo-700 hover:to-purple-700 transition-colors font-bold shadow-lg hover:shadow-xl"
                >
                  <MapIcon className="w-5 h-5" />
                  Implementation Roadmap
                </button>
              )}
            </>
          )}

          </div>
        </div>
      )}

      {showWorkflows && (
        <WorkflowViewer
          opportunityId={opportunity.id}
          opportunityTitle={opportunity.title}
          onClose={() => setShowWorkflows(false)}
        />
      )}

      {showAssistant && (
        <AIAssistant
          opportunityId={opportunity.id}
          opportunityTitle={opportunity.title}
          onClose={() => setShowAssistant(false)}
        />
      )}

      {showProcessFlow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold">Process Flow Transformation: {opportunity.title}</h2>
              <button
                onClick={() => setShowProcessFlow(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <ProcessFlowComparison
                opportunityId={opportunity.id}
                projectId={projectId}
                opportunityTitle={opportunity.title}
              />
            </div>
          </div>
        </div>
      )}

      {showArchitecture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold">Technical Architecture: {opportunity.title}</h2>
              <button
                onClick={() => setShowArchitecture(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <ArchitectureDiagram
                opportunityId={opportunity.id}
                projectId={projectId}
                opportunityTitle={opportunity.title}
              />
            </div>
          </div>
        </div>
      )}

      {showAIWorkflow && (
        <AIWorkflowModal
          opportunity={opportunity}
          projectId={projectId}
          onClose={() => setShowAIWorkflow(false)}
        />
      )}

      {showRoadmap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold">Implementation Roadmap: {opportunity.title}</h2>
              <button
                onClick={() => setShowRoadmap(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <PhasedRoadmap
                phasedPlan={opportunity.phased_rollout_plan || {total_phases: 0, recommended_starting_phase: 1}}
                recommendedPlatform={opportunity.recommended_platform}
                recommendedApproach={opportunity.recommended_approach}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityCard;
